/**
 * Policy アーカイブページ: intro セクションアニメーション
 *
 * SF_CorporateSite/assets/JS/policy.js の動作をそのまま再現。
 *
 * 1. 画像: スクロールで clip-path が両サイドから全幅に広がる（trigger: intro, center→top）
 * 2. テキスト: intro をピン留めして body が y: innerHeight → 0 へスライドアップ
 *
 * @package Spice_Theme
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/** 現在の viewport 幅から clip-path の両サイドオフセットを計算 */
function calcClipSide() {
  const remPx = window.innerWidth / 144;
  return (window.innerWidth - 128 * remPx) / 2;
}

// ページロード・遷移フェード（NEWS一覧と同方式）
const contentEl = document.querySelector('.p-policy-content');
if (contentEl) {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // clip-path の初期値をフェードイン前に適用してパカッを防ぐ
  const mediaEl = document.querySelector('.p-policy-intro__media');
  if (mediaEl && !reduced) {
    const clipSide = calcClipSide();
    gsap.set(mediaEl, {
      clipPath: `inset(0px ${clipSide}px 0px ${clipSide}px round 8px)`,
    });
  }

  if (reduced) {
    gsap.set(contentEl, { opacity: 1 });
  } else {
    gsap.to(contentEl, {
      opacity: 1,
      ease: 'power3.out',
      duration: 0.8,
      delay: 0.2,
    });

    document.addEventListener('click', e => {
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.defaultPrevented) return;
      const link = e.target.closest('a');
      if (!link) return;
      if (link.hasAttribute('download')) return;
      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('javascript:'))
        return;
      if (link.target && link.target !== '_self') return;
      try {
        if (new URL(href, location.href).origin !== location.origin) return;
      } catch {
        return;
      }
      e.preventDefault();
      let navigated = false;
      const navigate = () => {
        if (navigated) return;
        navigated = true;
        window.location.href = href;
      };
      gsap.to(contentEl, {
        opacity: 0,
        duration: 0.2,
        ease: 'power3.in',
        onComplete: navigate,
      });
      setTimeout(navigate, 400);
    });

    window.addEventListener('pageshow', e => {
      if (e.persisted) gsap.set(contentEl, { opacity: 1 });
    });
  }
}

window.addEventListener('load', () => {
  const intro = document.querySelector('.p-policy-intro');
  const media = document.querySelector('.p-policy-intro__media');
  const body = document.querySelector('.p-policy-intro__body');

  if (!intro || !media || !body) return;

  // GSAP ロード失敗時のフォールバック: visibility: hidden のままにならないよう保険をかける
  const revealBodyFallback = () => {
    body.style.visibility = 'visible';
  };

  // SF_CorporateSite は html { font-size: calc(100vw / 1440) } の fluid rem を使用
  // （1rem = 100vw / 144 px、1440px 時に 10px）
  // WP テーマは固定 10px のため、同じ計算式で clipSide が 1280px 以下で 0 以下になる
  // → SF と同じ fluid rem ベースで計算することで全ビューポートで正値を保つ
  let clipAnim;

  const buildClipAnimation = () => {
    if (clipAnim) clipAnim.kill();
    const clipSide = calcClipSide();

    clipAnim = gsap.fromTo(
      media,
      {
        clipPath: `inset(0px ${clipSide}px 0px ${clipSide}px round 8px)`,
      },
      {
        clipPath: 'inset(0px 0px 0px 0px round 0px)',
        ease: 'none',
        scrollTrigger: {
          trigger: intro,
          start: 'top center',
          end: 'top top',
          scrub: 1.5,
        },
      }
    );
  };

  try {
    // 1. 画像: 両サイドのclip-pathがスクロールで広がる
    buildClipAnimation();
    // ウィンドウリサイズ時に clip-path の初期値を再計算
    ScrollTrigger.addEventListener('refreshInit', buildClipAnimation);

    // 2. テキスト: intro をピン留めして body をスライドアップ
    // CSS で visibility: hidden に設定済みのため GSAP で visible に戻す
    gsap.set(body, { y: window.innerHeight, visibility: 'visible' });

    gsap.to(body, {
      y: 0,
      ease: 'none',
      scrollTrigger: {
        trigger: intro,
        start: 'top top',
        end: () => `+=${body.offsetHeight}`,
        pin: true,
        scrub: 1,
      },
    });
  } catch {
    // GSAP アニメーション失敗時: body を即時表示してコンテンツを見えるようにする
    revealBodyFallback();
  }
});
